import { Injectable } from "@angular/core";

export interface IScopedCssController {
  hostClass: string;
  mount(hostElement: HTMLElement): void;
  update(css: string): void;
  destroy(): void;
}

@Injectable({
  providedIn: "root",
})
export class ScopedPanelCssService {
  create(panelClassName: string): IScopedCssController {
    let hostElement: HTMLElement | null = null;
    let styleElement: HTMLStyleElement | null = null;
    let scopedCss = "";
    const scopeClass = this.createScopeClass(panelClassName);

    const removeStyleElement = () => {
      if (
        hostElement &&
        styleElement &&
        styleElement.parentNode === hostElement
      ) {
        hostElement.removeChild(styleElement);
      }

      styleElement = null;
    };

    const syncStyleElement = () => {
      if (!hostElement) {
        return;
      }

      if (!scopedCss) {
        removeStyleElement();
        return;
      }

      if (!styleElement) {
        styleElement = hostElement.ownerDocument.createElement("style");
        hostElement.insertBefore(styleElement, hostElement.firstChild);
      }

      styleElement.textContent = scopedCss;
    };

    return {
      hostClass: scopeClass,
      mount: (element: HTMLElement) => {
        if (hostElement !== element) {
          removeStyleElement();
          hostElement = element;
          hostElement.classList.add(scopeClass);
        }

        syncStyleElement();
      },
      update: (css: string) => {
        scopedCss = css ? this.scopeCssRules(css, `.${scopeClass}`) : "";
        syncStyleElement();
      },
      destroy: () => {
        removeStyleElement();

        if (hostElement) {
          hostElement.classList.remove(scopeClass);
        }

        hostElement = null;
      },
    };
  }

  private createScopeClass(panelClassName: string): string {
    return `${panelClassName}--scope-${Math.random()
      .toString(36)
      .slice(2, 10)}`;
  }

  private scopeCssRules(css: string, scopeSelector: string): string {
    return this.scopeCssBlocks(this.removeCssComments(css), scopeSelector);
  }

  private scopeCssBlocks(css: string, scopeSelector: string): string {
    let result = "";
    let cursor = 0;

    while (cursor < css.length) {
      const blockStart = css.indexOf("{", cursor);

      if (blockStart === -1) {
        break;
      }

      const selectorText = css.slice(cursor, blockStart).trim();
      const blockEnd = this.findBlockEnd(css, blockStart);

      if (!selectorText || blockEnd === -1) {
        break;
      }

      const blockContent = css.slice(blockStart + 1, blockEnd).trim();

      if (
        selectorText.indexOf("@media") === 0 ||
        selectorText.indexOf("@supports") === 0
      ) {
        result += `${selectorText} { ${this.scopeCssBlocks(
          blockContent,
          scopeSelector,
        )} }\n`;
      } else if (selectorText.indexOf("@") === 0) {
        result += `${selectorText} { ${blockContent} }\n`;
      } else {
        result += `${this.prefixSelectorList(
          selectorText,
          scopeSelector,
        )} { ${blockContent} }\n`;
      }

      cursor = blockEnd + 1;
    }

    return result.trim();
  }

  private findBlockEnd(css: string, blockStart: number): number {
    let depth = 0;

    for (let index = blockStart; index < css.length; index++) {
      if (css[index] === "{") {
        depth++;
      } else if (css[index] === "}") {
        depth--;

        if (depth === 0) {
          return index;
        }
      }
    }

    return -1;
  }

  private removeCssComments(css: string): string {
    return css.replace(/\/\*[\s\S]*?\*\//g, "");
  }

  private prefixSelectorList(
    selectorList: string,
    scopeSelector: string,
  ): string {
    const selectors: string[] = [];
    let currentSelector = "";
    let parenthesesDepth = 0;
    let bracketDepth = 0;

    for (let index = 0; index < selectorList.length; index++) {
      const character = selectorList[index];

      if (character === "(") {
        parenthesesDepth++;
      } else if (character === ")") {
        parenthesesDepth--;
      } else if (character === "[") {
        bracketDepth++;
      } else if (character === "]") {
        bracketDepth--;
      } else if (
        character === "," &&
        parenthesesDepth === 0 &&
        bracketDepth === 0
      ) {
        selectors.push(currentSelector);
        currentSelector = "";
        continue;
      }

      currentSelector += character;
    }

    if (currentSelector) {
      selectors.push(currentSelector);
    }

    return selectors
      .map((selector) => this.prefixSingleSelector(selector, scopeSelector))
      .join(", ");
  }

  private prefixSingleSelector(
    selector: string,
    scopeSelector: string,
  ): string {
    const trimmedSelector = selector.trim();

    if (!trimmedSelector) {
      return trimmedSelector;
    }

    if (trimmedSelector.indexOf(scopeSelector) === 0) {
      return trimmedSelector;
    }

    if (
      trimmedSelector === "html" ||
      trimmedSelector === "body" ||
      trimmedSelector === ":root" ||
      trimmedSelector === ":host" ||
      trimmedSelector === ":scope" ||
      trimmedSelector === "&"
    ) {
      return scopeSelector;
    }

    if (trimmedSelector.indexOf("&") === 0) {
      return trimmedSelector.replace("&", scopeSelector);
    }

    return `${scopeSelector} ${trimmedSelector}`;
  }
}
